// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import React from 'react';
import { Box, Typography } from '@mui/material';
import { CollateralPosition } from '@daml.js/utility-collateral-app-v1/lib/Utility/Collateral/App/Types/module';
import { useModuleParty } from '../../../contexts/ModuleContext';
import { fmt } from '../../../utils/common';

interface PositionsProps {
  positions: CollateralPosition[];
}
interface PositionProps {
  isPledgor: boolean;
  instrumentId: string;
  amount: string;
}
export const Position: React.FC<PositionProps> = ({ isPledgor, instrumentId, amount }) => {
  const color = isPledgor ? undefined : 'success.main';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Typography variant="body2" color={color}>
        {isPledgor ? String.fromCodePoint(0x2212) : '+'}
      </Typography>
      <Typography variant="body2" color={color} sx={{ mr: 1 }}>
        {amount}
      </Typography>
      <Typography variant="body2" color={color}>
        {instrumentId}
      </Typography>
    </Box>
  );
};

export const Positions: React.FC<PositionsProps> = ({ positions }) => {
  const party = useModuleParty();
  if (positions.length === 0) {
    return <Typography variant="body2">{fmt(0, 2)}</Typography>;
  }
  const displayedPositions = positions.map((p) => {
    const isPledgor = party === p.pledgor;
    return (
      <Position
        key={p.instrument.id}
        isPledgor={isPledgor}
        amount={fmt(p.amount, 2)}
        instrumentId={p.instrument.id}
      />
    );
  });
  return <Box sx={{ display: 'flex', flexDirection: 'column' }}>{displayedPositions}</Box>;
};
